import { useEffect, useState } from 'react';
import { defineMessages } from 'react-intl';

import { useBinaryState } from '../../../../hooks/use-binary-state';
import { getCarrierSharingDefaults } from '../api';
import { loadData } from '../utils';

const messages = defineMessages({
  title: {
    description: 'Title for Error Pop Up when Default Carriers failed to load.',
    defaultMessage: 'Failed to Load Default Carrier',
    id: '++e4dG',
  },
  content: {
    description: 'Content for Error Pop Up when Default Carrier failed to load.',
    defaultMessage: 'An unexpected error occurred while loading default carriers.',
    id: 'SrbN6u',
  },
});

const defaultCarrierSharing = {
  selectedServices: [],
};

/**
 * Fetches carrier sharing and updates the state accordingly.
 * @param {() => void} setStartLoading - A function to set the loading state to "true".
 * @param {(arg: object) => void} setDefaults - A function to set the default carrierSharing data.
 *    The provided function should set the state to an object with the following shape:
 *    { isDefault: boolean, isRequired: boolean, selectedServices: array }
 * @param {() => void} setStopLoading - A function to set the loading state to "false".
 * @returns {void} This function does not return a value directly.
 * Instead, it updates state using the provided callback functions.
 */
const fetchServices = (setStartLoading, setDefaults, setStopLoading) => {
  setStartLoading();
  loadData(
    getCarrierSharingDefaults(),
    (data) => setDefaults(data ?? defaultCarrierSharing),
    messages.title,
    messages.content,
    setStopLoading,
  );
};

/**
 * @typedef {object} UseCarrierSharing
 * @property {boolean} isLoading - A boolean indicating whether the data is loading.
 * @property {object} data - The carrier sharing data.
 * @property {(data: object) => void} setData - A function to set the carrier sharing data.
 */

/**
 * Custom hook for managing carrier sharing carrierSharing.
 * @returns {UseCarrierSharing} An object containing isLoading, carrierSharing, and setCarrierSharing properties.
 */
export const useCarrierSharing = () => {
  const [data, setData] = useState(defaultCarrierSharing);
  const [isLoading, setStartLoading, setStopLoading] = useBinaryState();

  useEffect(() => {
    fetchServices(setStartLoading, setData, setStopLoading);
  }, [setStartLoading, setStopLoading]);

  return { isLoading, data, setData };
};
